/* eslint-disable max-lines */
import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import CommonHeader from '../../../components/Headers/CommonHeader';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import { colors, layout } from '../NewHire/v2/styles';
import AnnualReviewService from '../../../services/AnnualReview';
import RatingSectionStep from './steps/RatingSectionStep';
import DirectorReviewStep from './steps/DirectorReviewStep';
import GrowthPlanModal from './GrowthPlanModal';
import PayRaiseStep from './PayRaiseStep';
import WaitingFinanceCard from './WaitingFinanceCard';
import PayRaiseDeclinedCard from './PayRaiseDeclinedCard';
import UploadSignedModal from './UploadSignedModal';
import FinishedReviewCard from './components/FinishedReviewCard';
import SupervisorReviewSummary from './components/SupervisorReviewSummary';
import {
  AnnualReviewResponse,
  AnnualReviewStepDescriptor,
  AnnualReviewSupervisorAnswers,
  resumeStepIndex,
  SUPERVISOR_STEPS,
} from './types';
import { ANNUAL_REVIEW_RATINGS, CORE_VALUES_SUBITEMS } from './utils/questionsCatalog';
import { computeSupervisorCompletedKeys } from './utils/supervisorCompletion';

const ALL_RATING_KEYS = [...ANNUAL_REVIEW_RATINGS.map((q) => q.key), ...CORE_VALUES_SUBITEMS.map((s) => s.key)];

type SupervisorPhase =
  | 'steps'
  | 'pay_raise_form'
  | 'pay_raise_declined'
  | 'growth_plan'
  | 'waiting_finance'
  | 'upload_signed'
  | 'finished';

type NextPhaseContext = { raiseDeclinedAck: boolean };

const payRaiseDenied = (review: AnnualReviewResponse): boolean => {
  return review.proposedPayRate?.decision?.outcome === 'denied';
};

const payRaisePending = (review: AnnualReviewResponse): boolean => {
  return !!review.createdPayRateId && !review.proposedPayRate?.decision;
};

const payRaiseResolved = (review: AnnualReviewResponse): boolean => {
  const skipped = !review.createdPayRateId && !!review.proposedPayRate;
  const decided = !!review.createdPayRateId && !!review.proposedPayRate?.decision;
  return skipped || decided;
};

const computeNextPhase = (review: AnnualReviewResponse, ctx: NextPhaseContext): SupervisorPhase | null => {
  if (review.status === 'FINISHED') return 'finished';
  if (review.status === 'CANCELED') return null;
  if (!review.supervisorCompletedAt) return null;
  if (payRaiseDenied(review) && !ctx.raiseDeclinedAck) return 'pay_raise_declined';
  if (payRaisePending(review)) return 'waiting_finance';
  if (payRaiseResolved(review)) return 'upload_signed';
  return 'pay_raise_form';
};

const SupervisorAnnualReviewFlow = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { token }: any = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [review, setReview] = useState<AnnualReviewResponse | null>(null);
  const [phase, setPhase] = useState<SupervisorPhase>('steps');
  const [stepIndex, setStepIndex] = useState(0);
  const [raiseDeclinedAck, setRaiseDeclinedAck] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [stepIndex, phase]);
  const [answers, setAnswers] = useState<AnnualReviewSupervisorAnswers>({
    ratings: {},
    examples: {},
    directorReview: { topStrengths: [], opportunitiesForGrowth: [] },
  });

  useEffect(() => {
    const load = async () => {
      try {
        if (!id || !token) return;
        const res = await AnnualReviewService(token).show(id);
        const data: AnnualReviewResponse = res.data;
        setReview(data);
        const mappedAnswers: AnnualReviewSupervisorAnswers | null = data.supervisorAnswers
          ? {
              ratings: data.supervisorAnswers.ratings || {},
              examples: data.supervisorAnswers.examples || {},
              directorReview: {
                topStrengths: data.supervisorAnswers.directorReview?.topStrengths || [],
                opportunitiesForGrowth: data.supervisorAnswers.directorReview?.opportunitiesForGrowth || [],
              },
            }
          : null;
        if (mappedAnswers) setAnswers(mappedAnswers);
        const next = computeNextPhase(data, { raiseDeclinedAck: false });
        if (next) {
          setPhase(next);
        } else {
          setStepIndex(resumeStepIndex(SUPERVISOR_STEPS, computeSupervisorCompletedKeys(mappedAnswers)));
        }
      } catch (e: any) {
        toast.error(e?.response?.data?.message || 'Could not load annual review');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, token]);

  const completedKeys = useMemo(() => computeSupervisorCompletedKeys(answers), [answers]);
  const steps: AnnualReviewStepDescriptor[] = SUPERVISOR_STEPS;

  const persistDraft = async (next: AnnualReviewSupervisorAnswers) => {
    if (!id || !token) return;
    try {
      await AnnualReviewService(token).submitSupervisorAnswers(id, { answers: next, isDraft: true });
    } catch (e) {
      // soft-fail
    }
  };

  const handleRatingChange = (key: string, value: number) => {
    const ratings = { ...(answers.ratings || {}), [key]: value };
    const next = { ...answers, ratings };
    setAnswers(next);
    persistDraft(next);
  };

  const handleExamplesChange = (sectionKey: string, value: string) => {
    const examples = { ...(answers.examples || {}), [sectionKey]: value };
    const next = { ...answers, examples };
    setAnswers(next);
    persistDraft(next);
  };

  const handleTopStrengthsChange = (index: number, value: string) => {
    const current = answers.directorReview?.topStrengths || [];
    const updated = [...current];
    updated[index] = value;
    const next = { ...answers, directorReview: { ...(answers.directorReview || {}), topStrengths: updated } };
    setAnswers(next);
    persistDraft(next);
  };

  const handleOpportunitiesChange = (index: number, value: string) => {
    const current = answers.directorReview?.opportunitiesForGrowth || [];
    const updated = [...current];
    updated[index] = value;
    const next = {
      ...answers,
      directorReview: { ...(answers.directorReview || {}), opportunitiesForGrowth: updated },
    };
    setAnswers(next);
    persistDraft(next);
  };

  const canSubmit = useMemo(() => {
    return ALL_RATING_KEYS.every((k) => typeof (answers.ratings || {})[k] === 'number');
  }, [answers]);

  const submitFinal = async () => {
    if (!id || !token) return;
    if (!canSubmit) {
      toast.error('Please rate every category before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await AnnualReviewService(token).submitSupervisorAnswers(id, { answers, isDraft: false });
      const data: AnnualReviewResponse = res.data;
      setReview(data);
      toast.success('Supervisor portion submitted.');
      const next = computeNextPhase(data, { raiseDeclinedAck });
      if (next) setPhase(next);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not submit supervisor portion');
    } finally {
      setSubmitting(false);
    }
  };

  const renderShell = (content: React.ReactNode) => (
    <>
      <CommonHeader />
      <Container className="mt--8 pt-3 mb-9" fluid>
        <Row className="justify-content-center mt-2">
          <Col md="10" lg="9">
            <Card className="shadow">
              <CardBody>{content}</CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );

  if (loading) {
    return renderShell(<p style={{ textAlign: 'center', color: colors.muted, margin: 0 }}>Loading…</p>);
  }

  if (!review) {
    return renderShell(
      <p style={{ textAlign: 'center', color: colors.danger, margin: 0 }}>Annual review not found.</p>,
    );
  }

  if (phase === 'pay_raise_form') {
    return renderShell(
      <PayRaiseStep
        review={review}
        token={token}
        onClose={() => history.push('/performance-management')}
        onBound={(updated) => {
          setReview(updated);
          const next = computeNextPhase(updated, { raiseDeclinedAck });
          if (next) setPhase(next);
        }}
        onSkip={async () => {
          try {
            const res = await AnnualReviewService(token).skipPayRate(review.id);
            setReview(res.data);
            const next = computeNextPhase(res.data, { raiseDeclinedAck });
            if (next) setPhase(next);
          } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Could not skip pay raise');
          }
        }}
      />,
    );
  }

  if (phase === 'growth_plan') {
    return renderShell(
      <GrowthPlanModal
        review={review}
        token={token}
        onClose={() => setPhase('upload_signed')}
        onSaved={(updated) => {
          setReview(updated);
          setPhase('upload_signed');
        }}
      />,
    );
  }

  if (phase === 'pay_raise_declined') {
    return renderShell(
      <PayRaiseDeclinedCard
        review={review}
        onResubmit={() => setPhase('pay_raise_form')}
        onContinue={() => {
          setRaiseDeclinedAck(true);
          setPhase('upload_signed');
        }}
      />,
    );
  }

  if (phase === 'waiting_finance') {
    return renderShell(<WaitingFinanceCard review={review} />);
  }

  if (phase === 'upload_signed') {
    return renderShell(
      <UploadSignedModal
        review={review}
        token={token}
        onClose={() => history.push('/performance-management')}
        onEditGrowthPlan={() => setPhase('growth_plan')}
        onUploaded={(updated) => {
          setReview(updated);
          setPhase('finished');
        }}
      />,
    );
  }

  if (phase === 'finished') {
    return renderShell(
      <FinishedReviewCard employeeName={review.employeeName} signedPdfUrl={review.finalSignedPdfUrl} />,
    );
  }

  const currentStep = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;
  const isReview = currentStep.key === 'review_submit';

  return renderShell(
    <>
      <div style={{ fontSize: 13, color: colors.muted, marginBottom: 6 }}>
        Step {stepIndex + 1} of {steps.length} · Reviewing {review.employeeName}
      </div>
      {['section_1', 'section_2', 'section_3', 'section_4'].includes(currentStep.key) ? (
        <RatingSectionStep
          section={Number(currentStep.key.split('_')[1]) as 1 | 2 | 3 | 4}
          ratings={answers.ratings || {}}
          examples={answers.examples || {}}
          onRatingChange={handleRatingChange}
          onExamplesChange={handleExamplesChange}
        />
      ) : null}
      {currentStep.key === 'director_review' ? (
        <DirectorReviewStep
          topStrengths={answers.directorReview?.topStrengths || []}
          opportunitiesForGrowth={answers.directorReview?.opportunitiesForGrowth || []}
          onTopStrengthsChange={handleTopStrengthsChange}
          onOpportunitiesChange={handleOpportunitiesChange}
        />
      ) : null}
      {isReview ? (
        <SupervisorReviewSummary
          steps={steps}
          completedKeys={completedKeys}
          answers={answers}
          onJumpToStep={(idx) => setStepIndex(idx)}
        />
      ) : null}

      <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'space-between' }}>
        <button
          type="button"
          onClick={() => (stepIndex === 0 ? history.push('/performance-management') : setStepIndex(stepIndex - 1))}
          style={layout.secondaryBtn}
        >
          <i className="fas fa-arrow-left" /> Back
        </button>
        <div style={{ display: 'flex', gap: 12 }}>
          <button type="button" onClick={() => history.push('/performance-management')} style={layout.secondaryBtn}>
            Save & Close
          </button>
          {isLast ? (
            <button
              type="button"
              onClick={submitFinal}
              disabled={submitting || !canSubmit}
              style={canSubmit && !submitting ? layout.primaryBtn : layout.disabledBtn}
            >
              {submitting ? 'Submitting…' : 'Submit Supervisor Portion'}
            </button>
          ) : (
            <button type="button" onClick={() => setStepIndex(stepIndex + 1)} style={layout.primaryBtn}>
              Next <i className="fas fa-arrow-right" />
            </button>
          )}
        </div>
      </div>
    </>,
  );
};

export default SupervisorAnnualReviewFlow;
